import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Scale } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/kb/page-header";
import { RuleCard } from "@/components/kb/rule-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useEntityMaps } from "@/hooks/useEntityMaps";
import {
	useCreateRule,
	useDeleteRule,
	useRules,
	useSetRuleEnabled,
} from "@/hooks/useRules";
import { rootRoute } from "./__root";

function RulesListComponent() {
	const navigate = useNavigate();
	const { data, isLoading, error } = useRules();
	const entities = useEntityMaps();
	const deleteRule = useDeleteRule();
	const createRule = useCreateRule();
	const setRuleEnabled = useSetRuleEnabled();

	if (isLoading || entities.isLoading) {
		return (
			<div className="flex flex-col gap-8">
				<PageHeader description="Loading your rules." title="Rules" />
				<div className="flex flex-col gap-3">
					<Skeleton className="h-36 w-full rounded-xl" />
					<Skeleton className="h-36 w-full rounded-xl" />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col gap-8">
				<PageHeader title="Rules" />
				<div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 text-sm text-destructive">
					Couldn&rsquo;t load rules: {error.message}
				</div>
			</div>
		);
	}

	const rules = data?.rules ?? [];

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				actions={
					<Button asChild className="hidden md:inline-flex">
						<Link to="/rules/create">
							<Plus />
							New rule
						</Link>
					</Button>
				}
				description={
					rules.length > 0
						? `${rules.length} ${rules.length === 1 ? "rule" : "rules"} · alerted when they hit`
						: "Nothing being watched yet."
				}
				title="Rules"
			/>

			{rules.length === 0 ? (
				<div className="flex flex-col items-center gap-4 rounded-xl border border-dashed px-6 py-16 text-center">
					<span className="flex size-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
						<Scale className="size-5" />
					</span>
					<div className="flex max-w-sm flex-col gap-1">
						<p className="font-medium">No rules yet.</p>
						<p className="text-sm text-muted-foreground">
							Describe what you want to catch, like “When Aaron Judge hits a
							home run, notify me.”
						</p>
					</div>
					<Button asChild>
						<Link to="/rules/create">
							<Plus />
							New rule
						</Link>
					</Button>
				</div>
			) : (
				<div className="flex flex-col gap-3">
					{rules.map((rule) => (
						<RuleCard
							busy={
								deleteRule.isPending ||
								createRule.isPending ||
								setRuleEnabled.isPending
							}
							key={rule.id}
							onDelete={() => {
								if (window.confirm("Delete this rule?")) {
									deleteRule.mutate(rule.id, {
										onError: (mutationError) =>
											toast.error("Could not delete rule", {
												description: mutationError.message,
											}),
										onSuccess: () => toast.success("Rule deleted"),
									});
								}
							}}
							onDuplicate={() => {
								createRule.mutate(
									{
										apps: rule.apps,
										condition: rule.rule,
										cooldown: rule.cooldown,
										enabled: rule.enabled,
										notifyAgainAfter: { unit: "minutes", value: 0 },
										prompt: rule.prompt,
									},
									{
										onError: (mutationError) =>
											toast.error("Could not duplicate rule", {
												description: mutationError.message,
											}),
										onSuccess: () => toast.success("Rule duplicated"),
									},
								);
							}}
							onEdit={() =>
								navigate({
									params: { ruleId: rule.id },
									to: "/rules/edit/$ruleId",
								})
							}
							onToggleEnabled={(enabled) =>
								setRuleEnabled.mutate(
									{ enabled, id: rule.id },
									{
										onError: (mutationError) =>
											toast.error("Could not update rule", {
												description: mutationError.message,
											}),
										onSuccess: () =>
											toast.success(enabled ? "Rule enabled" : "Rule paused"),
									},
								)
							}
							players={entities.players}
							rule={rule}
							teams={entities.teams}
						/>
					))}
				</div>
			)}
		</div>
	);
}

export const rulesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/rules",
	component: RulesListComponent,
});
